import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "./client";
import publicApi from "./publicClient";
import type { AxiosError } from "axios";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BillingPlanFeature {
  feature_key: string;
  feature_value: string | null;
}

export interface BillingPlan {
  plan_id: string;
  plan_key: string;
  display_name: string;
  description: string | null;
  stripe_price_id: string | null;
  price_monthly: number | null;
  currency: string | null;
  features: BillingPlanFeature[];
}

export interface BillingPlansResponse {
  plans: BillingPlan[];
}

export interface PublicBillingPlan {
  plan_id: string;
  plan_key: string;
  display_name: string;
  description: string | null;
  price_monthly: number | null;
  currency: string | null;
  marketing_bullets: string[];
  sort_order: number;
}

export interface PublicBillingPlansResponse {
  plans: PublicBillingPlan[];
}

export interface CurrentSubscription {
  subscription_id: string | null;
  plan_id: string | null;
  plan_key: string | null;
  plan_name: string | null;
  status: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  access_state?: string | null;
  trial_plan_id?: string | null;
  trial_plan_key?: string | null;
  trial_start?: string | null;
  trial_end?: string | null;
  days_remaining?: number | null;
  payment_method_status?: string | null;
  trial_provisioning_status?: string | null;
  selected_post_trial_plan?: {
    plan_id: string;
    plan_key: string;
    display_name: string;
    price_monthly: number | null;
    currency: string | null;
  } | null;
  primary_action?: string | null;
}

export interface CurrentSubscriptionResponse {
  subscription: CurrentSubscription | null;
}

export interface CheckoutSessionRequest {
  plan_id: string;
}

export interface CheckoutSessionResponse {
  checkout_url: string;
}

export type CheckoutEligibilityStatus =
  | "eligible"
  | "already_subscribed"
  | "retry_incomplete"
  | "invalid_plan";

export interface CheckoutIntentRequest {
  plan_key?: string;
}

export interface CheckoutIntentPlan {
  plan_id: string;
  plan_key: string;
  display_name: string;
  description: string | null;
  price_monthly: number | null;
  currency: string | null;
  marketing_bullets: string[];
}

export interface CheckoutIntentResponse {
  eligibility_status: CheckoutEligibilityStatus;
  can_checkout: boolean;
  message?: string;
  redirect_target?: string;
  recommended_action?: string;
  plan?: CheckoutIntentPlan;
}

export interface CustomerPortalSessionResponse {
  portal_url: string;
}

export interface TrialSelectPlanRequest {
  plan_id: string;
}

export interface TrialSelectPlanResponse {
  subscription: {
    status: string;
    selected_post_trial_plan: {
      plan_id: string;
      plan_key: string;
      display_name: string;
      price_monthly: number | null;
      currency: string | null;
    };
    first_billing_date: string | null;
    payment_method_status: string;
  };
}

export function normalizeMarketingBullets(value: unknown): string[] {
  const candidate = typeof value === "string" ? tryParseMarketingBullets(value) : value;
  if (!Array.isArray(candidate)) {
    return [];
  }

  return candidate
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length > 0 && item !== "[]");
}

function tryParseMarketingBullets(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "[]") {
    return [];
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return [trimmed];
  }
}

function normalizePublicBillingPlan(plan: PublicBillingPlan): PublicBillingPlan {
  return {
    ...plan,
    marketing_bullets: normalizeMarketingBullets(plan.marketing_bullets),
  };
}

function normalizeCheckoutIntentResponse(response: CheckoutIntentResponse): CheckoutIntentResponse {
  if (!response.plan) {
    return response;
  }

  return {
    ...response,
    plan: {
      ...response.plan,
      marketing_bullets: normalizeMarketingBullets(response.plan.marketing_bullets),
    },
  };
}

export function getSubscriptionTimingLabel(subscription: CurrentSubscription | null | undefined) {
  if (!subscription?.current_period_end) {
    return null;
  }
  if (subscription.status === "trialing") {
    return "Trial ends";
  }
  return subscription.cancel_at_period_end ? "Ends on" : "Renews on";
}

export function getSubscriptionStatusLabel(subscription: CurrentSubscription | null | undefined) {
  switch (subscription?.status) {
    case "active":
      return "Active";
    case "trialing":
      return "Trial";
    case "manual":
      return "Manual";
    case "past_due":
      return "Past due";
    case "canceled":
      return "Canceled";
    case "incomplete":
      return "Incomplete";
    case "unpaid":
      return "Unpaid";
    case "paused":
      return "Paused";
    case "incomplete_expired":
      return "Incomplete expired";
    default:
      return "No active subscription";
  }
}

export function getBillingActionErrorMessage(error: unknown) {
  const axiosError = error as AxiosError<{ error?: string }> | undefined;
  return (
    axiosError?.response?.data?.error ??
    "Billing management is unavailable right now. Please try again."
  );
}

// ---------------------------------------------------------------------------
// Fetch functions
// ---------------------------------------------------------------------------

const fetchBillingPlans = async (): Promise<BillingPlansResponse> => {
  const res = await api.get<BillingPlansResponse>("/billing/plans");
  return res.data;
};

const fetchCurrentSubscription = async (): Promise<CurrentSubscriptionResponse> => {
  const res = await api.get<CurrentSubscriptionResponse>("/billing/current");
  return res.data;
};

const fetchPublicBillingPlans = async (): Promise<PublicBillingPlansResponse> => {
  const res = await publicApi.get<PublicBillingPlansResponse>("/public/billing/plans");
  return {
    plans: res.data.plans.map(normalizePublicBillingPlan),
  };
};

const createCheckoutIntent = async (
  body: CheckoutIntentRequest
): Promise<CheckoutIntentResponse> => {
  const res = await api.post<CheckoutIntentResponse>("/billing/checkout-intent", body);
  return normalizeCheckoutIntentResponse(res.data);
};

const createCheckoutSession = async (
  body: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> => {
  const res = await api.post<CheckoutSessionResponse>("/billing/checkout-session", body);
  return res.data;
};

const createCustomerPortalSession = async (): Promise<CustomerPortalSessionResponse> => {
  const res = await api.post<CustomerPortalSessionResponse>("/billing/customer-portal-session");
  return res.data;
};

const selectTrialPlan = async (
  body: TrialSelectPlanRequest
): Promise<TrialSelectPlanResponse> => {
  const res = await api.post<TrialSelectPlanResponse>("/billing/trial/select-plan", body);
  return res.data;
};

const createTrialPaymentMethodSession = async (): Promise<CheckoutSessionResponse> => {
  const res = await api.post<CheckoutSessionResponse>("/billing/trial/payment-method-session");
  return res.data;
};

const retryTrialProvisioning = async (): Promise<CurrentSubscriptionResponse> => {
  const res = await api.post<CurrentSubscriptionResponse>("/billing/trial/retry");
  return res.data;
};

// ---------------------------------------------------------------------------
// TanStack Query hooks
// ---------------------------------------------------------------------------

export function useBillingPlans() {
  return useQuery({
    queryKey: ["billing", "plans"],
    queryFn: fetchBillingPlans,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCurrentSubscription(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ["billing", "current"],
    queryFn: fetchCurrentSubscription,
    refetchInterval: options?.refetchInterval,
    staleTime: 30 * 1000,
  });
}

export function usePublicBillingPlans() {
  return useQuery({
    queryKey: ["billing", "publicPlans"],
    queryFn: fetchPublicBillingPlans,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCheckoutIntent(planKey: string | null) {
  return useQuery({
    queryKey: ["billing", "checkoutIntent", planKey],
    queryFn: () =>
      createCheckoutIntent(planKey ? { plan_key: planKey } : {}),
  });
}

export function useCheckoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["billing", "current"] });
      window.location.href = data.checkout_url;
    },
  });
}

export function useCustomerPortalSessionMutation() {
  return useMutation({
    mutationFn: createCustomerPortalSession,
    onSuccess: (data) => {
      window.location.href = data.portal_url;
    },
  });
}

export function useTrialPlanSelectionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: selectTrialPlan,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing", "current"] });
    },
  });
}

export function useTrialPaymentMethodSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createTrialPaymentMethodSession,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["billing", "current"] });
      window.location.href = data.checkout_url;
    },
  });
}

export function useTrialRetryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryTrialProvisioning,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing", "current"] });
    },
  });
}
